import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerProfileApi, messagesApi, ordersApi, subscriptionsApi } from '../services/api';
import type { Conversation, Order } from '../types';

type BusinessSubscriptionLite = {
  id: string;
  businessId?: string;
  businessName?: string;
  status?: string;
  amount?: number;
};

type BusinessRelationship = {
  businessId: string;
  businessName: string;
  isWebsiteProfile?: boolean;
  websiteHost?: string;
  businessMangoId?: string;
  ordersCount: number;
  totalSpent: number;
  lastOrderAt?: string;
  subscriptionsCount: number;
  activeSubscriptions: number;
  conversationsCount: number;
  unreadCount: number;
  lastMessageAt?: string;
};

type CommPreference = {
  newsletters: boolean;
  promotions: boolean;
  sales: boolean;
  marketing: boolean;
  messageUpdates: boolean;
  orderUpdates: boolean;
  subscriptionUpdates: boolean;
};

const PREFERENCE_KEY = 'mango_personal_business_crm_preferences_v1';

const defaultPreference: CommPreference = {
  newsletters: true,
  promotions: true,
  sales: true,
  marketing: true,
  messageUpdates: true,
  orderUpdates: true,
  subscriptionUpdates: true,
};

const DOMAIN_KEY_REGEX = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

const isDomainPreferenceKey = (key: string): boolean => {
  if (!key) return false;
  if (key.includes('://') || key.includes(':https://') || key.includes(':http://')) return true;
  return DOMAIN_KEY_REGEX.test(key);
};

const getHostFromPreferenceKey = (key: string): string => {
  if (!key) return 'website';
  if (key.includes(':https://')) {
    return key.split(':https://')[0] || 'website';
  }
  if (key.includes(':http://')) {
    return key.split(':http://')[0] || 'website';
  }
  if (key.includes('://')) {
    try {
      return new URL(key).hostname;
    } catch {
      return key;
    }
  }
  return key;
};

const BusinessCRM: React.FC = () => {
  const navigate = useNavigate();
  const [relationships, setRelationships] = useState<BusinessRelationship[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [preferencesByBusiness, setPreferencesByBusiness] = useState<Record<string, CommPreference>>({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const loadCRM = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const [ordersRes, convRes, subRes, prefsRes] = await Promise.all([
        ordersApi.getMyOrders(1, 200),
        messagesApi.getConversations(1),
        subscriptionsApi.getMySubscriptions(),
        customerProfileApi.getPreferences().catch(() => null),
      ]);

      const orderList = (ordersRes?.data ?? ordersRes?.orders ?? []) as Order[];
      const convList = (convRes?.data ?? []) as Conversation[];
      const subList = ((subRes?.data ?? subRes?.subscriptions ?? []) as BusinessSubscriptionLite[]) || [];

      const map = new Map<string, BusinessRelationship>();

      (Array.isArray(orderList) ? orderList : []).forEach((order) => {
        const id = order.businessId || order.business?.id || `biz:${order.businessName || order.business?.name || 'unknown'}`;
        const name = order.businessName || order.business?.name || 'Business';
        const existing = map.get(id) || {
          businessId: id,
          businessName: name,
          businessMangoId: order.businessMangoId || order.business?.mangoId,
          ordersCount: 0,
          totalSpent: 0,
          subscriptionsCount: 0,
          activeSubscriptions: 0,
          conversationsCount: 0,
          unreadCount: 0,
        };
        existing.ordersCount += 1;
        existing.totalSpent += Number(order.total || 0);
        if (!existing.lastOrderAt || new Date(order.createdAt).getTime() > new Date(existing.lastOrderAt).getTime()) {
          existing.lastOrderAt = order.createdAt;
        }
        map.set(id, existing);
      });

      (Array.isArray(convList) ? convList : []).forEach((conversation) => {
        const id = conversation.businessId || `biz:${conversation.businessName || 'unknown'}`;
        const existing = map.get(id) || {
          businessId: id,
          businessName: conversation.businessName || 'Business',
          businessMangoId: conversation.businessMangoId,
          ordersCount: 0,
          totalSpent: 0,
          subscriptionsCount: 0,
          activeSubscriptions: 0,
          conversationsCount: 0,
          unreadCount: 0,
        };
        existing.conversationsCount += 1;
        existing.unreadCount += Number(conversation.unreadCount || 0);
        if (
          conversation.lastMessageAt &&
          (!existing.lastMessageAt || new Date(conversation.lastMessageAt).getTime() > new Date(existing.lastMessageAt).getTime())
        ) {
          existing.lastMessageAt = conversation.lastMessageAt;
        }
        map.set(id, existing);
      });

      (Array.isArray(subList) ? subList : []).forEach((subscription) => {
        const id = subscription.businessId || `biz:${subscription.businessName || 'unknown'}`;
        const existing = map.get(id) || {
          businessId: id,
          businessName: subscription.businessName || 'Business',
          ordersCount: 0,
          totalSpent: 0,
          subscriptionsCount: 0,
          activeSubscriptions: 0,
          conversationsCount: 0,
          unreadCount: 0,
        };
        existing.subscriptionsCount += 1;
        if (subscription.status === 'active') existing.activeSubscriptions += 1;
        map.set(id, existing);
      });

      const remoteNotificationPrefs = (prefsRes as { preferences?: { notificationPreferences?: Record<string, unknown> } } | null)?.preferences?.notificationPreferences || {};
      const remoteByDomain = (
        remoteNotificationPrefs &&
        typeof remoteNotificationPrefs === 'object' &&
        (remoteNotificationPrefs as { businessByDomain?: Record<string, CommPreference> }).businessByDomain
      ) || {};

      Object.keys(remoteByDomain || {}).forEach((domainKey) => {
        if (map.has(domainKey)) return;
        const host = getHostFromPreferenceKey(domainKey);
        map.set(domainKey, {
          businessId: domainKey,
          businessName: host,
          isWebsiteProfile: true,
          websiteHost: host,
          ordersCount: 0,
          totalSpent: 0,
          subscriptionsCount: 0,
          activeSubscriptions: 0,
          conversationsCount: 0,
          unreadCount: 0,
        });
      });

      const sorted = Array.from(map.values()).sort((a, b) => {
        const aTime = a.lastOrderAt || a.lastMessageAt || '';
        const bTime = b.lastOrderAt || b.lastMessageAt || '';
        return new Date(bTime || 0).getTime() - new Date(aTime || 0).getTime();
      });
      setRelationships(sorted);
      if (sorted.length > 0 && !selectedBusinessId) {
        setSelectedBusinessId(sorted[0].businessId);
      }

      const localRaw = localStorage.getItem(PREFERENCE_KEY);
      const localPrefs = localRaw ? (JSON.parse(localRaw) as Record<string, CommPreference>) : {};

      const remoteByBusiness = (
        remoteNotificationPrefs &&
        typeof remoteNotificationPrefs === 'object' &&
        (remoteNotificationPrefs as { businessCRMByBusiness?: Record<string, CommPreference> }).businessCRMByBusiness
      ) || {};
      const normalizedByDomain = Object.fromEntries(
        Object.entries(remoteByDomain || {}).map(([key, value]) => [key, { ...defaultPreference, ...value }])
      ) as Record<string, CommPreference>;

      setPreferencesByBusiness({ ...normalizedByDomain, ...remoteByBusiness, ...localPrefs });
    } catch (err) {
      console.error('Failed to load business CRM:', err);
      setError('Failed to load business relationship CRM.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedBusinessId]);

  useEffect(() => {
    loadCRM();
  }, [loadCRM]);

  const filteredRelationships = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return relationships;
    return relationships.filter((relationship) => relationship.businessName.toLowerCase().includes(q));
  }, [relationships, search]);

  const selected = filteredRelationships.find((relationship) => relationship.businessId === selectedBusinessId)
    || relationships.find((relationship) => relationship.businessId === selectedBusinessId)
    || null;

  const selectedPreferences = selected
    ? (preferencesByBusiness[selected.businessId] || defaultPreference)
    : defaultPreference;

  const updateSelectedPreference = (key: keyof CommPreference, value: boolean) => {
    if (!selected) return;
    setPreferencesByBusiness((prev) => ({
      ...prev,
      [selected.businessId]: {
        ...(prev[selected.businessId] || defaultPreference),
        [key]: value,
      },
    }));
    setSaveMessage(null);
  };

  const handleSavePreferences = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      localStorage.setItem(PREFERENCE_KEY, JSON.stringify(preferencesByBusiness));
      const currentPreferences = await customerProfileApi.getPreferences().catch(() => null);
      const existingNotificationPreferences = (
        currentPreferences as { preferences?: { notificationPreferences?: Record<string, unknown> } } | null
      )?.preferences?.notificationPreferences || {};
      const existingBusinessByDomain = (
        existingNotificationPreferences &&
        typeof existingNotificationPreferences === 'object' &&
        (existingNotificationPreferences as { businessByDomain?: Record<string, CommPreference> }).businessByDomain
      ) || {};
      const existingBusinessCRMByBusiness = (
        existingNotificationPreferences &&
        typeof existingNotificationPreferences === 'object' &&
        (existingNotificationPreferences as { businessCRMByBusiness?: Record<string, CommPreference> }).businessCRMByBusiness
      ) || {};

      const domainPrefs = Object.fromEntries(
        Object.entries(preferencesByBusiness).filter(([key]) => isDomainPreferenceKey(key))
      ) as Record<string, CommPreference>;
      const businessPrefs = Object.fromEntries(
        Object.entries(preferencesByBusiness).filter(([key]) => !isDomainPreferenceKey(key))
      ) as Record<string, CommPreference>;

      await customerProfileApi.updatePreferences({
        notificationPreferences: {
          ...existingNotificationPreferences,
          businessByDomain: {
            ...existingBusinessByDomain,
            ...domainPrefs,
          },
          businessCRMByBusiness: {
            ...existingBusinessCRMByBusiness,
            ...businessPrefs,
          },
        },
      });
      setSaveMessage('Preferences saved.');
    } catch (err) {
      console.error('Failed to persist preferences remotely, saved locally:', err);
      setSaveMessage('Saved locally on this device. Remote sync unavailable right now.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <div style={{ fontSize: '42px' }}>🧭</div>
        <p style={{ color: '#666', marginTop: '14px' }}>Loading Business CRM...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>🧭 Business Hub</h1>
          <p style={{ margin: '8px 0 0 0', color: '#666' }}>
            Manage every business you interact with: conversations, orders, subscriptions, and communication preferences.
          </p>
        </div>
        <button
          type="button"
          onClick={() => loadCRM(true)}
          disabled={refreshing}
          style={{ padding: '10px 16px', borderRadius: '10px', border: '1px solid #ffb387', background: '#fff7f2', color: '#c2410c', cursor: refreshing ? 'not-allowed' : 'pointer', fontWeight: 600 }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: '14px', padding: '12px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '14px' }}>
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search businesses you interact with..."
          style={{ width: '100%', maxWidth: '420px', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '14px' }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '18px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', maxHeight: '70vh', overflow: 'auto' }}>
          <h3 style={{ marginTop: 0, fontSize: '16px', color: '#333' }}>Businesses ({filteredRelationships.length})</h3>
          {filteredRelationships.length === 0 ? (
            <p style={{ color: '#666', marginBottom: 0 }}>No business relationships found yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredRelationships.map((relationship) => {
                const isSelected = selectedBusinessId === relationship.businessId;
                return (
                  <button
                    key={relationship.businessId}
                    onClick={() => setSelectedBusinessId(relationship.businessId)}
                    style={{ textAlign: 'left', border: isSelected ? '1px solid #ff9a56' : '1px solid #e5e7eb', background: isSelected ? '#fff7f2' : 'white', borderRadius: '10px', padding: '10px', cursor: 'pointer' }}
                  >
                    <div style={{ fontWeight: 700, color: '#333', marginBottom: '4px' }}>{relationship.businessName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {relationship.ordersCount} orders • {relationship.subscriptionsCount} subscriptions • {relationship.conversationsCount} conversations
                    </div>
                    {relationship.unreadCount > 0 && (
                      <div style={{ marginTop: '4px', fontSize: '12px', color: '#b45309', fontWeight: 600 }}>
                        {relationship.unreadCount} unread message{relationship.unreadCount !== 1 ? 's' : ''}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {!selected ? (
            <p style={{ color: '#666', margin: 0 }}>Select a business to manage relationship settings.</p>
          ) : (
            <>
              <h2 style={{ marginTop: 0, marginBottom: '8px', color: '#333' }}>{selected.businessName}</h2>
              {selected.isWebsiteProfile && (
                <p style={{ marginTop: 0, marginBottom: '10px', color: '#64748b', fontSize: '13px' }}>
                  Website profile imported from a business frontend ({selected.websiteHost || selected.businessName}).
                </p>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '18px' }}>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Orders</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: '#111827' }}>{selected.ordersCount}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Subscriptions</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: '#111827' }}>{selected.activeSubscriptions}/{selected.subscriptionsCount}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Conversations</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: '#111827' }}>{selected.conversationsCount}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Spent</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: '#111827' }}>${selected.totalSpent.toFixed(2)}</div>
                </div>
              </div>

              <h3 style={{ marginBottom: '10px', color: '#333' }}>Communication Controls</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                {([
                  ['newsletters', 'Newsletters'],
                  ['promotions', 'Promotions'],
                  ['sales', 'Sales Alerts'],
                  ['marketing', 'Marketing Content'],
                  ['messageUpdates', 'Conversation Updates'],
                  ['orderUpdates', 'Order Updates'],
                  ['subscriptionUpdates', 'Subscription Updates'],
                ] as Array<[keyof CommPreference, string]>).map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <span style={{ color: '#374151', fontSize: '14px' }}>{label}</span>
                    <input
                      type="checkbox"
                      checked={selectedPreferences[key]}
                      onChange={(event) => updateSelectedPreference(key, event.target.checked)}
                    />
                  </label>
                ))}
              </div>

              <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {!selected.isWebsiteProfile && (
                  <button
                    type="button"
                    onClick={() => navigate(`/orders?businessId=${encodeURIComponent(selected.businessId)}`)}
                    style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: 'white', color: '#1e293b', cursor: 'pointer', fontWeight: 600 }}
                  >
                    View Orders
                  </button>
                )}
                {!selected.isWebsiteProfile && (
                  <button
                    type="button"
                    onClick={() => navigate(`/subscriptions?businessId=${encodeURIComponent(selected.businessId)}`)}
                    style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: 'white', color: '#1e293b', cursor: 'pointer', fontWeight: 600 }}
                  >
                    View Subscriptions
                  </button>
                )}
                {!selected.isWebsiteProfile && (
                  <button
                    type="button"
                    onClick={() => navigate(`/messages?businessId=${encodeURIComponent(selected.businessId)}`)}
                    style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: 'white', color: '#1e293b', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Open Conversations
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  disabled={saving}
                  style={{ padding: '10px 16px', borderRadius: '10px', border: 'none', background: '#ff6b35', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                >
                  {saving ? 'Saving…' : 'Save Preferences'}
                </button>
                {saveMessage && <span style={{ fontSize: '13px', color: '#166534' }}>{saveMessage}</span>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessCRM;
